import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as CopyIcon } from '../../../../../assets/icons/copy.svg';
import { ReactComponent as RfidTagIcon } from '../../../../../assets/icons/rfid-icon.svg';
import { ReactComponent as SyncIcon } from '../../../../../assets/icons/sync.svg';
import { ReactComponent as ZapIcon } from '../../../../../assets/icons/zap.svg';
import { useAppSelector } from '../../../../../redux/hooks';
import BatteryLoader from '../../../../../shared-components/battery-loader/BatteryLoader';
import ConvertUTCtoDate from '../../../../../shared-functions/ConvertUTCtoDate';
import rfidApi from '../../../../../utils/api/rfid';

/**
 * Interface representing a customer RFID token entity
 */
export interface CustomerRfidToken {
  _id: string;
  uid: string;
  type: 'PERSONAL_TAP' | 'BUSINESS_TAP' | string;
  status: 'ACTIVE' | 'DEACTIVATED' | string;
  tenantId?: string;
  customerId?: string | null;
  cardPoolId?: string | null;
  homeCardPoolId?: string | null;
  issuedBy?: string;
  deactivatedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
  __v?: number;
  stationIds?: string[];
  balanceKwh?: number | { $numberDecimal: string } | string;
  relationship?: 'own' | 'linked' | string;
  sync?: {
    status?: string;
    attempts?: number;
    lastAttemptAt?: string;
    lastError?: string | null;
    ocpiReferenceId?: string;
  };
}

/**
 * Interface for RfidCard component properties
 */
export interface RfidCardProperties {
  customerId?: string;
}

/**
 * Safely extracts numeric balance in kWh from token
 * @param {any} balanceKwh - Raw energy balance value
 * @returns {number} Numeric balance in kWh
 */
const getNumericBalance = (balanceKwh: any): number => {
  if (typeof balanceKwh === 'object' && balanceKwh !== null && '$numberDecimal' in balanceKwh) {
    return Number.parseFloat(balanceKwh.$numberDecimal) || 0;
  }
  if (typeof balanceKwh === 'number') {
    return balanceKwh;
  }
  if (typeof balanceKwh === 'string') {
    return Number.parseFloat(balanceKwh) || 0;
  }
  return 0;
};

/**
 * Resolves CSS modifier class for relationship badge
 * @param {string} [relationship] - Relationship type string
 * @returns {string} Badge modifier class
 */
const getRelationshipClass = (relationship?: string): string => {
  if (relationship === 'own') return 'own';
  if (relationship === 'linked') return 'linked';
  return '';
};

/**
 * Side panel component for displaying customer RFID cards and details
 * @param {RfidCardProperties} properties - Component properties
 * @returns {React.JSX.Element} The RfidCard side panel component
 */
function RfidCard(properties: RfidCardProperties): React.JSX.Element {
  const { customerId: propertyCustomerId } = properties;
  const { t, i18n } = useTranslation('endConsumer');
  const { selectedEndConsumer } = useAppSelector((state) => state.endConsumerType);

  const [rfidCards, setRfidCards] = useState<CustomerRfidToken[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [copiedUid, setCopiedUid] = useState<string | null>(null);

  const effectiveCustomerId = propertyCustomerId || selectedEndConsumer?.userCustomId || '';

  /**
   * Fetches customer RFID tokens from the EMSP API
   * @param {string} consumerId - Customer unique identifier
   * @returns {Promise<void>}
   */
  const fetchCustomerTokens = useCallback(
    async (consumerId: string): Promise<void> => {
      if (!consumerId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const response = await rfidApi.fetchTokensByCustomer(consumerId);
        const data = response?.data;
        let tokenList: CustomerRfidToken[] = [];

        if (Array.isArray(data)) {
          tokenList = data;
        } else if (Array.isArray(data?.results)) {
          tokenList = data.results;
        } else if (Array.isArray(data?.data)) {
          tokenList = data.data;
        }

        setRfidCards(tokenList);
      } catch (error) {
        console.error('Failed to fetch customer RFID tokens:', error);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (effectiveCustomerId) {
      fetchCustomerTokens(effectiveCustomerId);
    } else {
      setLoading(false);
    }
  }, [effectiveCustomerId, fetchCustomerTokens]);

  /**
   * Copies token UID to user clipboard
   * @param {string} uid - Token UID to copy
   * @returns {void}
   */
  const handleCopyUid = (uid: string): void => {
    if (!uid) return;
    navigator.clipboard.writeText(uid);
    setCopiedUid(uid);
    setTimeout(() => {
      setCopiedUid(null);
    }, 2000);
  };

  const activeCount = rfidCards.filter((card) => card.status === 'ACTIVE').length;

  return (
    <div className="rfid-side-pane-wrap" dir={i18n.dir()}>
      {loading ? (
        <div className="rfid-loader">
          <BatteryLoader />
        </div>
      ) : (
        <>
          {/* Summary Header */}
          <div className="rfid-summary-header">
            <div className="summary-left">
              <div className="summary-icon">
                <RfidTagIcon />
              </div>
              <p className="summary-title">{t('rfidCards', 'RFID Cards')}</p>
            </div>
            <div className="summary-stats">
              <div className="stat-chip">
                <span>{t('total', 'Total')}:</span>
                <span className="chip-count">{rfidCards.length}</span>
              </div>
              {rfidCards.length > 0 && (
                <div className="stat-chip active-chip">
                  <span>{t('active', 'Active')}:</span>
                  <span className="chip-count">{activeCount}</span>
                </div>
              )}
            </div>
          </div>

          {/* Cards List or Empty State */}
          {rfidCards.length === 0 ? (
            <div className="rfid-empty-state">
              <div className="empty-icon">
                <RfidTagIcon />
              </div>
              <p className="empty-title">{t('noRfidCardsFound', 'No RFID Cards Found')}</p>
              <p className="empty-description">
                {t(
                  'noRfidCardsDescription',
                  'There are no RFID cards assigned to or linked with this customer.',
                )}
              </p>
            </div>
          ) : (
            <div className="rfid-cards-list">
              {rfidCards.map((card) => {
                const isBusiness = card.type === 'BUSINESS_TAP';
                const isActive = card.status === 'ACTIVE';
                const numericBalance = getNumericBalance(card.balanceKwh);

                return (
                  <div key={card._id || card.uid} className="rfid-card-item">
                    {/* Top Row: UID + Badges */}
                    <div className="card-top-row">
                      <div className="card-uid-block">
                        <span className="rfid-tag-icon">
                          <RfidTagIcon />
                        </span>
                        <p className="uid-number">{card.uid}</p>
                        <button
                          type="button"
                          className="copy-btn"
                          title={t('copyUid', 'Copy UID')}
                          onClick={() => handleCopyUid(card.uid)}>
                          <CopyIcon />
                        </button>
                        {copiedUid === card.uid && (
                          <span className="copied-text">{t('copied', 'Copied!')}</span>
                        )}
                      </div>

                      <div className="card-badges-row">
                        {/* Type Badge */}
                        <span className={`badge-type ${isBusiness ? 'business' : 'personal'}`}>
                          {isBusiness
                            ? t('businessTap', 'Business Tap')
                            : t('personalTap', 'Personal Tap')}
                        </span>

                        {/* Status Badge */}
                        <span className={`badge-status ${isActive ? 'active' : 'deactivated'}`}>
                          <span className="status-dot" />
                          {card.status}
                        </span>

                        {/* Relationship Badge */}
                        {card.relationship && (
                          <span
                            className={`badge-relation ${getRelationshipClass(card.relationship)}`}>
                            {card.relationship}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Details Grid */}
                    <div className="card-info-grid">
                      {/* Balance (kWh) */}
                      <div className="info-cell">
                        <p className="cell-label">{t('energyBalance', 'Energy Balance')}</p>
                        <p className="cell-value highlight-energy">
                          <ZapIcon className="energy-icon" />
                          {numericBalance} kWh
                        </p>
                      </div>

                      {/* Issued By */}
                      <div className="info-cell">
                        <p className="cell-label">{t('issuedBy', 'Issued By')}</p>
                        <p className="cell-value">{card.issuedBy || '-'}</p>
                      </div>

                      {/* Customer ID */}
                      <div className="info-cell">
                        <p className="cell-label">{t('customerId', 'Customer ID')}</p>
                        <p className="cell-value">{card.customerId || '-'}</p>
                      </div>

                      {/* Card Pool / Group ID */}
                      {(card.cardPoolId || card.homeCardPoolId) && (
                        <div className="info-cell">
                          <p className="cell-label">{t('cardPool', 'Card Pool ID')}</p>
                          <p className="cell-value">
                            {card.cardPoolId || card.homeCardPoolId || '-'}
                          </p>
                        </div>
                      )}

                      {/* Sync Status */}
                      {card.sync?.status && (
                        <div className="info-cell">
                          <p className="cell-label">{t('syncStatus', 'Sync Status')}</p>
                          <p className="cell-value">
                            <span className="badge-sync">
                              <SyncIcon />
                              {card.sync.status}
                            </span>
                          </p>
                        </div>
                      )}

                      {/* OCPI Reference ID */}
                      {card.sync?.ocpiReferenceId && (
                        <div className="info-cell full-width">
                          <p className="cell-label">{t('ocpiReference', 'OCPI Reference')}</p>
                          <p className="cell-value">{card.sync.ocpiReferenceId}</p>
                        </div>
                      )}

                      {/* Assigned Stations */}
                      {card.stationIds && card.stationIds.length > 0 && (
                        <div className="info-cell full-width">
                          <p className="cell-label">
                            {t('assignedStations', 'Assigned Stations')} ({card.stationIds.length})
                          </p>
                          <div className="stations-list-tag-container">
                            {card.stationIds.map((stationId) => (
                              <span key={stationId} className="station-tag">
                                {stationId}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Created At */}
                      {card.createdAt && (
                        <div className="info-cell">
                          <p className="cell-label">{t('createdAt', 'Created At')}</p>
                          <p className="cell-value">{ConvertUTCtoDate(card.createdAt)}</p>
                        </div>
                      )}

                      {/* Updated At */}
                      {card.updatedAt && (
                        <div className="info-cell">
                          <p className="cell-label">{t('updatedAt', 'Updated At')}</p>
                          <p className="cell-value">{ConvertUTCtoDate(card.updatedAt)}</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default RfidCard;
